import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { CalendarIcon, ChevronDown, MapPin } from "lucide-react";
import {
  useCreateStockOut,
  useListInventoryItems,
  useListInventoryCategories,
  useListStockLocations,
  getListStockOutQueryKey,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ItemPicker } from "./item-picker";

// Phase 2.1 polish G — generic non-member stock-out. Member-attached consumption
// is deferred to Phase 3 (Member Billing) where it'll live as its own module.
// For now, Stock-Out is just "stuff leaves the warehouse for any reason":
// staff use, wastage, damage, expiry, samples, transfers out.

const NON_MEMBER_TYPES = [
  { value: "Staff_Use", label: "Staff Use", hint: "Day-to-day staff consumption" },
  { value: "Wastage", label: "Wastage", hint: "Spillage, breakage, expiry-after-open" },
  { value: "Damaged", label: "Damaged", hint: "Damaged-in-storage" },
  { value: "Expired", label: "Expired", hint: "Past expiry date" },
  { value: "Sample_Out", label: "Sample Out", hint: "Marketing / vendor sample" },
  { value: "Transfer_Out", label: "Transfer Out", hint: "Sent to another space / branch" },
] as const;

const STORAGE_LOCATIONS = ["Pantry", "Store_Room", "IT_Rack", "Cabin_Store", "Reception", "Office_Floor"] as const;

const StockOutSchema = z.object({
  transactionDate: z.date({ required_error: "Pick a date" }),
  consumptionType: z.enum(NON_MEMBER_TYPES.map((t) => t.value) as unknown as [string, ...string[]]),
  itemId: z.coerce.number().int().positive("Pick an item"),
  quantity: z.coerce.number().positive("Quantity must be > 0"),
  issuedFrom: z.enum(STORAGE_LOCATIONS).nullable().optional(),
  reason: z.string().nullable().optional(),
  // Phase 58 Plan 13 (LOC-01) — "Consume from" against the real stock_locations
  // table. Distinct from the legacy free-text `issuedFrom` above. Defaults to
  // the item's usual sale location and is overridable, but is never a
  // blocking field — the counter flow is built for speed (58-CONTEXT.md).
  locationId: z.coerce.number().nullable().optional(),
});

type FormValues = z.infer<typeof StockOutSchema>;

interface Props {
  onSaved?: () => void;
  onDiscard?: () => void;
}

export function StockOutForm({ onSaved, onDiscard }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: items = [] } = useListInventoryItems({ status: "Active" });
  const { data: categories = [] } = useListInventoryCategories();
  const { data: stockLocations = [] } = useListStockLocations();
  const createMutation = useCreateStockOut();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  // Phase 58 Plan 13 (LOC-01) — "Consume from" is pre-filled and rendered
  // collapsed (a single line, not an open Select) so it never slows the
  // counter flow down; expanding it is opt-in.
  const [locationExpanded, setLocationExpanded] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(StockOutSchema),
    defaultValues: {
      transactionDate: new Date(),
      consumptionType: "Staff_Use",
      itemId: undefined as unknown as number,
      quantity: 1,
      issuedFrom: null,
      reason: null,
      locationId: null,
    },
  });

  // General Consumption covers the free, member-less charging models:
  // Complimentary_Unlimited (coffee beans, glass, stirrer…) and Internal_Use
  // (toilet roll, cleaning supplies, kitchenware, stationery). Internal_Use had
  // no stock-out form at all before, so those items could never be consumed.
  // With-Limit and Pay-Per-Use have their own forms for their charging models.
  // Write-off reasons (Wastage / Damaged / Expired) apply to ANY stock, including
  // Pay-Per-Use and With-Limit items that expire or break on the shelf — so for
  // those reasons the picker lists every active item, not just the free ones.
  const watchedType = form.watch("consumptionType");
  const isWriteOff = watchedType === "Wastage" || watchedType === "Damaged" || watchedType === "Expired";
  const sortedItems = useMemo(() => {
    const generalCatIds = new Set(
      categories
        .filter((c) => c.chargingModel === "Complimentary_Unlimited" || c.chargingModel === "Internal_Use")
        .map((c) => c.id),
    );
    return items
      .filter((it) => isWriteOff || generalCatIds.has(it.categoryId))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items, categories, isWriteOff]);

  const activeStockLocations = useMemo(() => stockLocations.filter((l) => l.isActive), [stockLocations]);
  const unassignedLocationId = useMemo(
    () => stockLocations.find((l) => l.isUnassignedBucket)?.id ?? null,
    [stockLocations],
  );

  const watchedItemId = form.watch("itemId");
  const watchedLocationId = form.watch("locationId");
  const locationName = stockLocations.find((l) => l.id === watchedLocationId)?.name ?? null;

  // Switching from a write-off reason back to e.g. Staff Use must not leave a
  // Pay-Per-Use item selected that the narrower list no longer offers.
  useEffect(() => {
    if (watchedItemId && !sortedItems.some((it) => it.id === watchedItemId)) {
      form.setValue("itemId", undefined as unknown as number);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedItems]);

  // Default "Consume from" to the picked item's usual sale location, else
  // Unassigned — every time the item changes. The operator can still
  // override it by expanding the collapsed row below.
  useEffect(() => {
    if (!watchedItemId) return;
    const picked = items.find((it) => it.id === watchedItemId);
    const defaultLoc = picked?.defaultSaleLocationId ?? unassignedLocationId;
    if (defaultLoc != null) form.setValue("locationId", defaultLoc, { shouldDirty: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedItemId, items, unassignedLocationId]);

  async function onSubmit(values: FormValues) {
    try {
      await createMutation.mutateAsync({
        data: {
          transactionDate: values.transactionDate.toISOString(),
          consumptionType: values.consumptionType as "Staff_Use" | "Wastage" | "Damaged" | "Expired" | "Sample_Out" | "Transfer_Out",
          itemId: values.itemId,
          quantityIssued: values.quantity,
          issuedFrom: values.issuedFrom ?? null,
          reason: values.reason ?? null,
          // Phase 58 Plan 13 (LOC-01): read directly off the raw request body
          // server-side (58-05's convention) — not yet in the orval-generated
          // CreateStockOutBody schema, so this rides along via the same cast
          // every other extra field on this payload already uses.
          locationId: values.locationId ?? null,
        } as unknown as Parameters<typeof createMutation.mutateAsync>[0]["data"],
      });
      toast({ title: "Stock-out recorded." });
      queryClient.invalidateQueries({ queryKey: getListStockOutQueryKey() });
      onSaved?.();
    } catch {
      toast({ title: "Could not save. Please try again.", variant: "destructive" });
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 px-4 pt-2 pb-6 max-w-md">
        <div>
          <h2 className="text-lg font-semibold">New Stock Out</h2>
          <p className="text-xs text-muted-foreground">General consumption / wastage / staff use. No member attached — member-attached billing comes in Phase 3.</p>
        </div>

        <FormField
          name="transactionDate"
          control={form.control}
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Date <span className="text-destructive">*</span></FormLabel>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {field.value ? format(field.value, "PPP") : "Pick a date"}
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={(d) => { if (d) { field.onChange(d); setDatePickerOpen(false); } }}
                    disabled={(d) => d > new Date()}
                    className="p-4 [--cell-size:2.5rem]"
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="consumptionType"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Reason <span className="text-destructive">*</span></FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                </FormControl>
                <SelectContent>
                  {NON_MEMBER_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      <div className="flex flex-col">
                        <span>{t.label}</span>
                        <span className="text-xs text-muted-foreground">{t.hint}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="itemId"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Item <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <ItemPicker
                  items={sortedItems}
                  value={field.value ?? null}
                  onChange={(id) => field.onChange(id)}
                  placeholder="Pick an item"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="quantity"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Quantity <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="e.g. 2"
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Phase 58 Plan 13 (LOC-01) — "Consume from" against the real
            stock_locations table. Pre-filled from the item's usual sale
            location and rendered collapsed; expanding it to override is
            opt-in so it never becomes a mandatory pick on every sale. */}
        <div className="rounded-md border bg-muted/30 px-3 py-2">
          {!locationExpanded ? (
            <button
              type="button"
              className="flex w-full items-center justify-between text-left"
              onClick={() => setLocationExpanded(true)}
            >
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" />
                Consuming from <span className="font-medium text-foreground">{locationName ?? "—"}</span>
              </span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                Change <ChevronDown className="h-3 w-3" />
              </span>
            </button>
          ) : (
            <FormField
              name="locationId"
              control={form.control}
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="text-xs">Consume from</FormLabel>
                  <Select
                    value={field.value != null ? String(field.value) : ""}
                    onValueChange={(v) => field.onChange(v ? Number(v) : null)}
                  >
                    <FormControl>
                      <SelectTrigger className="h-9"><SelectValue placeholder="Pick a location" /></SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {activeStockLocations.map((l) => (
                        <SelectItem key={l.id} value={String(l.id)}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          )}
        </div>

        <FormField
          name="issuedFrom"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>From Location</FormLabel>
              <Select value={field.value ?? ""} onValueChange={(v) => field.onChange(v || null)}>
                <FormControl>
                  <SelectTrigger><SelectValue placeholder="Optional — where it came from" /></SelectTrigger>
                </FormControl>
                <SelectContent>
                  {STORAGE_LOCATIONS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="reason"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Optional — short note (e.g. 'broken during cleanup')"
                  value={field.value ?? ""}
                  onChange={(e) => field.onChange(e.target.value || null)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onDiscard}>Discard</Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? "Saving…" : "Save Stock Out"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
